import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { isPartnerRole } from "./lib/partners";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { PlanProvider } from "./contexts/PlanContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import PackagesPage from "./pages/PackagesPage";
import CustomersPage from "./pages/CustomersPage";
import InvoicesPage from "./pages/InvoicesPage";
import PaymentsPage from "./pages/PaymentsPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import TrackingPage from "./pages/TrackingPage";
import PublicInvoicePage from "./pages/PublicInvoicePage";
import ReportsPage from "./pages/ReportsPage";
import AccountingPage from "./pages/AccountingPage";
import BillingPage from "./pages/BillingPage";
import PortalLoginPage from "./pages/PortalLoginPage";
import PortalDashboardPage from "./pages/PortalDashboardPage";
import LandingPage from "./pages/LandingPage";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import TrackAnyPackage from "./pages/TrackAnyPackage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import AdminDashboard from "./pages/AdminDashboard";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ClientSubmitPage from "./pages/ClientSubmitPage";
import ClientOnboardPage from "./pages/ClientOnboardPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import "./App.css";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children, blockWarehouse = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center ship-gradient">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-white font-medium">Cargando / Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Warehouse workers can ONLY use /packages.
  // Everything else kicks them to /packages (their landing page).
  if (user.role === 'warehouse') {
    const path = location.pathname;
    const allowed = path.startsWith('/packages');
    if (!allowed) {
      return <Navigate to="/packages" replace />;
    }
  }

  // Freddy / AJ viewer are view-only — allowed on /customers and /packages (and their sub-paths).
  if (isPartnerRole(user.role)) {
    const path = location.pathname;
    const allowed = path.startsWith('/customers') || path.startsWith('/packages');
    if (!allowed) {
      return <Navigate to="/customers" replace />;
    }
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <PlanProvider>
      <LanguageProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                borderRadius: '8px',
              }
            }}
          />
          <PWAInstallPrompt />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/track-package" element={<TrackAnyPackage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/track" element={<TrackingPage />} />
            <Route path="/track/:trackingNumber" element={<TrackingPage />} />
            <Route path="/invoice/:invoiceNumber" element={<PublicInvoicePage />} />
            <Route path="/portal" element={<PortalLoginPage />} />
            <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/packages"
              element={
                <ProtectedRoute>
                  <PackagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounting"
              element={
                <ProtectedRoute>
                  <AccountingPage />
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            <Route path="/super-admin" element={<SuperAdminLogin />} />
            <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
            <Route path="/portal/submit/:linkId" element={<ClientSubmitPage />} />
            <Route path="/join/:companyId" element={<ClientOnboardPage />} />
            <Route path="/join/:companyId/:sourceTag" element={<ClientOnboardPage />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
      </PlanProvider>
    </AuthProvider>
  );
}

export default App;
